import apiClient, { API_ENDPOINTS } from '../config/api';

/**
 * API Service Layer - Centralized API calls
 */

// Services API
export const servicesApi = {
  getAll: async () => {
    try {
      const currency = 'INR'; // Default currency
      return await apiClient.get(`${API_ENDPOINTS.services}?currency=${currency}`);
    } catch (error) {
      console.error('Failed to fetch services:', error.message);
      // Return empty array to prevent crashes
      return { data: [] };
    }
  },
  getById: async (id) => {
    try {
      return await apiClient.get(API_ENDPOINTS.serviceById(id));
    } catch (error) {
      console.error(`Failed to fetch service ${id}:`, error.message);
      return { data: null };
    }
  },
};

// Products API
export const productsApi = {
  getAll: async () => {
    try {
      const currency = 'INR'; // Default currency
      return await apiClient.get(`${API_ENDPOINTS.products}?currency=${currency}`);
    } catch (error) {
      console.error('Failed to fetch products:', error.message);
      // Return empty array to prevent crashes
      return { data: [] };
    }
  },
  getById: async (id) => {
    try {
      return await apiClient.get(API_ENDPOINTS.productById(id));
    } catch (error) {
      console.error(`Failed to fetch product ${id}:`, error.message);
      return { data: null };
    }
  },
};

// Cart API - Robust error handling to prevent app crashes
export const cartApi = {
  getCart: async (sessionId = 'default') => {
    try {
      console.log(`[API] Getting cart - sessionId: ${sessionId}`);
      
      // Use path parameter format: GET /api/cart/{sessionId}
      const url = `${API_ENDPOINTS.cart}/${sessionId}`;
      console.log(`[API] GET request to: ${url}`);
      const response = await apiClient.get(url);
      console.log(`[API] Get cart response:`, response.data);
      return response;
    } catch (error) {
      const status = error.response?.status;
      console.error(`[API] Get cart error - Status: ${status}, Message:`, error.message);
      
      // Handle any error gracefully - return empty cart instead of crashing
      if (status === 404 || status === 500 || !error.response) {
        console.warn(`Cart error (${status || 'network'}), returning empty cart...`);
        return {
          data: {
            sessionId: sessionId,
            items: [],
            totalAmount: 0,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          }
        };
      }
      
      // For other errors, log and return empty cart (don't crash)
      console.error('Cart fetch error:', error.message);
      return {
        data: {
          sessionId: sessionId,
          items: [],
          totalAmount: 0
        }
      };
    }
  },
  
  addItem: async (item, sessionId = 'default') => {
    try {
      console.log(`[API] Adding item to cart - sessionId: ${sessionId}, item:`, item);
      // API expects: POST /api/cart?sessionId=default
      // With body: { productId, name, price, quantity, image }
      const url = `${API_ENDPOINTS.cart}?sessionId=${sessionId}`;
      console.log(`[API] POST request to: ${url}`);
      const response = await apiClient.post(url, item);
      console.log(`[API] Add item response:`, response.data);
      return response;
    } catch (error) {
      const status = error.response?.status;
      console.error(`[API] Add item error - Status: ${status}, Message:`, error.message);
      console.error(`[API] Full error:`, error);
      
      if (status === 405) {
        // 405 Method Not Allowed - Try alternative
        console.warn('POST /api/cart?sessionId= failed, trying alternative...');
        try {
          return await apiClient.post(API_ENDPOINTS.cartAddItem, item);
        } catch (innerError) {
          console.error('Both cart add endpoints failed:', innerError.message);
          // Don't crash - return success structure anyway
          return { data: { success: false } };
        }
      }
      
      // Handle cart doesn't exist - still return success
      if (status === 404 || status === 500) {
        console.warn(`Cart not found (${status}), item may not be added`);
        return { data: { success: false, message: 'Cart not found' } };
      }
      
      // Log error but don't crash
      console.error('Add to cart failed:', error.message);
      return { data: { success: false } };
    }
  },
  
  updateItem: async (itemId, quantity, sessionId = 'default') => {
    try {
      // Try PUT endpoint
      return await apiClient.put(`${API_ENDPOINTS.cartUpdate}?sessionId=${sessionId}`, { itemId, quantity });
    } catch (error) {
      const status = error.response?.status;
      
      if (status === 405) {
        // 405 Method Not Allowed - Try POST instead
        console.warn('PUT /api/cart/update failed, trying POST...');
        try {
          return await apiClient.post(`${API_ENDPOINTS.cartUpdate}?sessionId=${sessionId}`, { itemId, quantity });
        } catch (innerError) {
          console.error('Update cart failed:', innerError.message);
          return { data: { success: false } };
        }
      }
      
      // Handle gracefully
      console.error('Update cart item failed:', error.message);
      return { data: { success: false } };
    }
  },
  
  removeItem: async (productId, sessionId = 'default') => {
    try {
      console.log(`[API] Removing item from cart - productId: ${productId}, sessionId: ${sessionId}`);
      // Use path parameter format: DELETE /api/cart/{sessionId}/item/{productId}
      const url = `${API_ENDPOINTS.cart}/${sessionId}/item/${productId}`;
      console.log(`[API] DELETE request to: ${url}`);
      return await apiClient.delete(url);
    } catch (error) {
      const status = error.response?.status;
      console.error(`[API] Remove item error - Status: ${status}:`, error.message);
      
      if (status === 405) {
        // 405 Method Not Allowed - Try POST instead
        console.warn('DELETE /api/cart endpoint failed, trying POST...');
        try {
          return await apiClient.post(`${API_ENDPOINTS.cartRemove}?sessionId=${sessionId}`, { productId });
        } catch (innerError) {
          console.error('Remove cart item failed:', innerError.message);
          return { data: { success: false } };
        }
      }
      
      // Handle gracefully - item might not exist
      if (status === 404) {
        console.warn('Cart item not found, may already be removed');
        return { data: { success: true } }; // Treat as success
      }
      
      console.error('Remove cart item failed:', error.message);
      return { data: { success: false } };
    }
  },

  clearCart: async (sessionId = 'default') => {
    try {
      console.log(`[API] Clearing cart - sessionId: ${sessionId}`);
      // DELETE /api/cart/{sessionId} - deletes entire cart
      const url = `${API_ENDPOINTS.cart}/${sessionId}`;
      console.log(`[API] DELETE cart request to: ${url}`);
      const response = await apiClient.delete(url);
      console.log(`[API] Clear cart response:`, response.data);
      return response;
    } catch (error) {
      const status = error.response?.status;
      console.error(`[API] Clear cart error - Status: ${status}, Message:`, error.message);
      
      // Handle gracefully - cart might not exist
      if (status === 404 || status === 500) {
        console.warn(`Cart clear failed (${status}), returning success anyway`);
        return { data: { success: true } };
      }
      
      console.error('Clear cart failed:', error.message);
      return { data: { success: false } };
    }
  },
};

// Checkout API
export const checkoutApi = {
  createOrder: async (orderData) => {
    try {
      // Explicitly get token and add to headers
      const token = localStorage.getItem('token');
      const user = localStorage.getItem('user');
      
      console.log('[API] Creating order - Auth check:', {
        hasToken: !!token,
        hasUser: !!user,
        tokenLength: token?.length || 0,
        tokenPreview: token ? token.substring(0, 30) + '...' : 'NO TOKEN'
      });
      
      if (!token) {
        console.error('[API] No token found in localStorage');
        const error = new Error('Authentication required. Please login.');
        error.response = { status: 401, data: { message: 'No token found' } };
        throw error;
      }
      
      console.log('[API] Creating order with data:', orderData);
      
      // Make request with explicit Authorization header
      const response = await apiClient.post(API_ENDPOINTS.checkout, orderData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('[API] Order created successfully:', response.data);
      return response;
    } catch (error) {
      const status = error.response?.status;
      console.error(`[API] Create order failed - Status: ${status}, Message:`, error.message);
      
      // Log the full error response for debugging
      if (error.response?.data) {
        console.error('[API] Error details:', error.response.data);
      }
      
      // Log request details
      console.error('[API] Failed request details:', {
        url: API_ENDPOINTS.checkout,
        hasAuthHeader: !!error.config?.headers?.Authorization,
        headers: error.config?.headers
      });
      
      // Re-throw the error so the caller can handle it
      throw error;
    }
  },
  getOrders: () => apiClient.get(API_ENDPOINTS.orders),
  getOrderById: (id) => apiClient.get(API_ENDPOINTS.orderById(id)),
};

// Contact API
export const contactApi = {
  sendMessage: (messageData) => apiClient.post(API_ENDPOINTS.contact, messageData),
};

// Gallery API
export const galleryApi = {
  getAll: async () => {
    try {
      const response = await apiClient.get(API_ENDPOINTS.gallery);
      console.log('[API] Get all gallery items:', response.data);
      return response;
    } catch (error) {
      console.error('[API] Failed to fetch gallery items:', error.message);
      return { data: [] };
    }
  },
  
  getById: async (id) => {
    try {
      return await apiClient.get(API_ENDPOINTS.galleryById(id));
    } catch (error) {
      console.error(`[API] Failed to fetch gallery item ${id}:`, error.message);
      return { data: null };
    }
  },
  
  create: async (itemData) => {
    try {
      console.log('[API] Creating gallery item:', itemData);
      const response = await apiClient.post(API_ENDPOINTS.gallery, itemData);
      console.log('[API] Gallery item created:', response.data);
      return response;
    } catch (error) {
      console.error('[API] Failed to create gallery item:', error.message);
      throw error;
    }
  },
  
  update: async (id, itemData) => {
    try {
      console.log('[API] Updating gallery item:', id, itemData);
      const response = await apiClient.put(API_ENDPOINTS.galleryById(id), itemData);
      console.log('[API] Gallery item updated:', response.data);
      return response;
    } catch (error) {
      console.error('[API] Failed to update gallery item:', error.message);
      throw error;
    }
  },
  
  delete: async (id) => {
    try {
      console.log('[API] Deleting gallery item:', id);
      const response = await apiClient.delete(API_ENDPOINTS.galleryById(id));
      console.log('[API] Gallery item deleted');
      return response;
    } catch (error) {
      console.error('[API] Failed to delete gallery item:', error.message);
      throw error;
    }
  },
  
  createWithImage: async (formData) => {
    try {
      console.log('[API] Creating gallery item with image (multipart)');
      const response = await apiClient.post(API_ENDPOINTS.galleryWithImage, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      console.log('[API] Gallery item created with image:', response.data);
      return response;
    } catch (error) {
      console.error('[API] Failed to create gallery item with image:', error.message);
      throw error;
    }
  }
};

// Product API (Admin)
export const adminProductsApi = {
  getAll: async () => {
    try {
      const response = await apiClient.get(API_ENDPOINTS.products);
      console.log('[API] Get all products:', response.data);
      return response;
    } catch (error) {
      console.error('[API] Failed to fetch products:', error.message);
      return { data: [] };
    }
  },
  
  getById: async (id) => {
    try {
      return await apiClient.get(API_ENDPOINTS.productById(id));
    } catch (error) {
      console.error(`[API] Failed to fetch product ${id}:`, error.message);
      return { data: null };
    }
  },
  
  create: async (productData) => {
    try {
      console.log('[API] Creating product:', productData);
      const response = await apiClient.post(API_ENDPOINTS.products, productData);
      console.log('[API] Product created:', response.data);
      return response;
    } catch (error) {
      console.error('[API] Failed to create product:', error.message);
      throw error;
    }
  },
  
  update: async (id, productData) => {
    try {
      console.log('[API] Updating product:', id, productData);
      const response = await apiClient.put(API_ENDPOINTS.productById(id), productData);
      console.log('[API] Product updated:', response.data);
      return response;
    } catch (error) {
      console.error('[API] Failed to update product:', error.message);
      throw error;
    }
  },
  
  delete: async (id) => {
    try {
      console.log('[API] Deleting product:', id);
      const response = await apiClient.delete(API_ENDPOINTS.productById(id));
      console.log('[API] Product deleted');
      return response;
    } catch (error) {
      console.error('[API] Failed to delete product:', error.message);
      throw error;
    }
  }
};

// Auth API (optional - for future use)
export const authApi = {
  login: (credentials) => apiClient.post(API_ENDPOINTS.login, credentials),
  register: (userData) => apiClient.post(API_ENDPOINTS.register, userData),
  logout: () => apiClient.post(API_ENDPOINTS.logout),
  getProfile: () => apiClient.get(API_ENDPOINTS.profile),
};

// Export all APIs
const apiService = {
  services: servicesApi,
  products: productsApi,
  cart: cartApi,
  checkout: checkoutApi,
  contact: contactApi,
  gallery: galleryApi,
  adminProducts: adminProductsApi,
  auth: authApi,
};

export default apiService;
